import React, { useState, useEffect } from 'react';
import {
  Button,
  message,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  WalletOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  FilterOutlined,
  GiftOutlined,
  CloseOutlined,
  LinkOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { NotiModal } from '../../components/Noti/NotiModal';
import { IncomeTransactionModalMobile } from '../../components/income/IncomeTransactionModalMobile';
import { TransactionDetailModalMobile as IncomeDetailModal } from '../../components/income/TransactionDetailModalMobile';
import { FilterModalMobile as IncomeFilterModal } from '../../components/income/FilterModalMobile';
import { ExpenseTransactionModalMobile } from '../../components/expenses/ExpenseTransactionModalMobile';
import { TransactionDetailModalMobile as ExpenseDetailModal } from '../../components/expenses/TransactionDetailModalMobile';
import { FilterModalMobile as ExpenseFilterModal } from '../../components/expenses/FilterModalMobile';
import {
  IncomeTransaction,
  IncomeSource,
  SourceWallet as IncomeSourceWallet,
  incomeService,
  formatCurrency as formatCurrencyIncome,
} from '../../services/incomeService';
import {
  ExpenseTransaction,
  ExpenseCategory,
  getExpenseTransactions,
  getExpenseCategories,
  deleteExpenseTransaction,
  formatCurrency as formatCurrencyExpense,
  WalletLink,
  getExpenseWalletLinks,
} from '../../services/expenseService';
import styles from './TransactionsPageMobile.module.css';

// Cấu hình locale tiếng Việt
dayjs.locale('vi');

// Combined transaction interface
interface CombinedTransaction {
  id: string; // Use string to differentiate between income and expense
  type: 'income' | 'expense';
  transaction: IncomeTransaction | ExpenseTransaction;
  date: string;
  amount: number;
  notes: string | null;
  category?: ExpenseCategory;
  source?: IncomeSource;
  wallet?: IncomeSourceWallet;
  walletLinks?: WalletLink[];
}

export const TransactionsPageMobile: React.FC = () => {
  const [combinedTransactions, setCombinedTransactions] = useState<CombinedTransaction[]>([]);
  const [incomeTransactions, setIncomeTransactions] = useState<IncomeTransaction[]>([]);
  const [expenseTransactions, setExpenseTransactions] = useState<ExpenseTransaction[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [sources, setSources] = useState<IncomeSource[]>([]);
  const [wallets, setWallets] = useState<IncomeSourceWallet[]>([]);
  const [walletLinks, setWalletLinks] = useState<{ [key: number]: WalletLink[] }>({});
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isIncomeModalOpen, setIsIncomeModalOpen] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [editingIncomeTransaction, setEditingIncomeTransaction] = useState<IncomeTransaction | undefined>();
  const [editingExpenseTransaction, setEditingExpenseTransaction] = useState<ExpenseTransaction | undefined>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<{ id: string; type: 'income' | 'expense' } | null>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedTransaction, setSelectedTransaction] = useState<CombinedTransaction | undefined>();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: undefined as 'income' | 'expense' | undefined,
    category_id: undefined as number | undefined,
    source_id: undefined as number | undefined,
    wallet_id: undefined as number | undefined,
    transaction_type: undefined as 'income' | undefined,
    search: '',
    start_date: undefined as string | undefined,
    end_date: undefined as string | undefined,
  });
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);

  // Load categories, sources, wallets đồng thời
  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [categoriesData, sourcesData, walletsData] = await Promise.all([
        getExpenseCategories(),
        incomeService.getSources(),
        incomeService.getWallets()
      ]);
      
      setCategories(categoriesData);
      setSources(sourcesData);
      setWallets(walletsData);
      setIsInitialDataLoaded(true);
    } catch (error) {
      console.error('Error loading initial data:', error);
      message.error('Không thể tải dữ liệu khởi tạo');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (reset = false) => {
    // Chỉ load transactions khi đã có dữ liệu categories, sources, wallets
    if (!isInitialDataLoaded) {
      return;
    }

    try {
      const pageToLoad = reset ? 1 : currentPage;
      
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // Load both income and expense transactions
      const [incomeResponse, expenseResponse] = await Promise.all([
        incomeService.getTransactions({
          page: pageToLoad,
          limit: pageSize,
          source_id: filters.source_id,
          wallet_id: filters.wallet_id,
          transaction_type: filters.transaction_type,
          search: filters.search,
          start_date: filters.start_date || currentMonth.startOf('month').format('YYYY-MM-DD'),
          end_date: filters.end_date || currentMonth.endOf('month').format('YYYY-MM-DD'),
        }),
        getExpenseTransactions({
          page: pageToLoad,
          limit: pageSize,
          category_id: filters.category_id,
          search: filters.search,
          start_date: filters.start_date || currentMonth.startOf('month').format('YYYY-MM-DD'),
          end_date: filters.end_date || currentMonth.endOf('month').format('YYYY-MM-DD'),
        })
      ]);

      // Filter by transaction type if specified
      let filteredIncome = incomeResponse.data;
      let filteredExpense = expenseResponse.data;

      if (filters.type === 'income') {
        filteredExpense = [];
      } else if (filters.type === 'expense') {
        filteredIncome = [];
      }

      // Combine and sort transactions
      const combined: CombinedTransaction[] = [
        ...filteredIncome.map(transaction => ({
          id: `income-${transaction.id}`,
          type: 'income' as const,
          transaction,
          date: transaction.income_date,
          amount: transaction.amount,
          notes: transaction.notes,
          source: sources.find(s => s.id === transaction.source_id),
          wallet: wallets.find(w => w.id === transaction.wallet_id),
        })),
        ...filteredExpense.map(transaction => ({
          id: `expense-${transaction.id}`,
          type: 'expense' as const,
          transaction,
          date: transaction.expense_date,
          amount: transaction.amount,
          notes: transaction.notes,
          category: categories.find(c => c.id === transaction.category_id),
        }))
      ];

      // Sort by created_at (newest first)
      combined.sort((a, b) => {
        const aCreatedAt = a.transaction.created_at || a.date;
        const bCreatedAt = b.transaction.created_at || b.date;
        return new Date(bCreatedAt).getTime() - new Date(aCreatedAt).getTime();
      });

      if (reset) {
        setCombinedTransactions(combined);
        setCurrentPage(1);
        // Load wallet links for expense transactions
        loadWalletLinksForTransactions(filteredExpense);
      } else {
        setCombinedTransactions(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const newTransactions = combined.filter(t => !existingIds.has(t.id));
          // Load wallet links for new expense transactions
          const newExpenseTransactions = newTransactions
            .filter(t => t.type === 'expense')
            .map(t => t.transaction as ExpenseTransaction);
          loadWalletLinksForTransactions(newExpenseTransactions);
          return [...prev, ...newTransactions];
        });
      }

      // Update pagination info
      const totalItems = (incomeResponse.pagination?.total || 0) + (expenseResponse.pagination?.total || 0);
      setTotal(totalItems);
      const maxPages = Math.max(
        incomeResponse.pagination?.totalPages || 0,
        expenseResponse.pagination?.totalPages || 0
      );
      setHasMore(pageToLoad < maxPages);

    } catch (error) {
      console.error('Error in loadTransactions:', error);
      message.error('Không thể tải danh sách giao dịch');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load wallet links for expense transactions
  const loadWalletLinksForTransactions = async (transactions: ExpenseTransaction[]) => {
    try {
      const linksPromises = transactions.map(async (transaction) => {
        try {
          const links = await getExpenseWalletLinks(transaction.id);
          return { transactionId: transaction.id, links };
        } catch (error) {
          console.error(`Error loading wallet links for transaction ${transaction.id}:`, error);
          return { transactionId: transaction.id, links: [] };
        }
      });

      const results = await Promise.all(linksPromises);
      const newWalletLinks: { [key: number]: WalletLink[] } = {};
      
      results.forEach(({ transactionId, links }) => {
        if (links.length > 0) {
          newWalletLinks[transactionId] = links;
        }
      });

      setWalletLinks(prev => ({ ...prev, ...newWalletLinks }));
    } catch (error) {
      console.error('Error loading wallet links:', error);
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore && !loading) {
      setCurrentPage(prev => prev + 1);
    }
  };



  // Effect để load dữ liệu khởi tạo
  useEffect(() => {
    loadInitialData();
  }, []);

  // Effect để load transactions khi initial data đã sẵn sàng
  useEffect(() => {
    if (isInitialDataLoaded && currentPage === 1) {
      loadTransactions(true);
    } else if (isInitialDataLoaded && currentPage > 1) {
      loadTransactions(false);
    }
  }, [isInitialDataLoaded, currentPage, pageSize]);

  // Effect để reset pagination khi filter thay đổi
  useEffect(() => {
    if (isInitialDataLoaded) {
      setCurrentPage(1);
      setHasMore(true);
      setCombinedTransactions([]);
      setTotal(0);
      setWalletLinks({});
      setTimeout(() => {
        loadTransactions(true);
      }, 100);
    }
  }, [filters, currentMonth, isInitialDataLoaded]);



  const handleAddIncomeTransaction = () => {
    setEditingIncomeTransaction(undefined);
    setIsIncomeModalOpen(true);
  };

  const handleAddExpenseTransaction = () => {
    setEditingExpenseTransaction(undefined);
    setIsExpenseModalOpen(true);
  };

  const handleEditIncomeTransaction = (record: IncomeTransaction) => {
    setEditingIncomeTransaction(record);
    setIsIncomeModalOpen(true);
    setIsDetailModalOpen(false);
  };

  const handleEditExpenseTransaction = (record: ExpenseTransaction) => {
    setEditingExpenseTransaction(record);
    setIsExpenseModalOpen(true);
    setIsDetailModalOpen(false);
  };

  const handleDeleteTransaction = async (id: string, type: 'income' | 'expense') => {
    try {
      const numericId = parseInt(id.split('-')[1]);
      
      if (type === 'income') {
        await incomeService.deleteTransaction(numericId);
      } else {
        await deleteExpenseTransaction(numericId);
      }
      
      message.success('Xóa giao dịch thành công');
      loadTransactions(true);
      if (type === 'income') {
        // Reload wallets for income transactions
        try {
          const walletsData = await incomeService.getWallets();
          setWallets(walletsData);
        } catch (error) {
          console.error('Error reloading wallets:', error);
        }
      }
      setIsDetailModalOpen(false);
    } catch (error: any) {
      // Hiển thị thông báo chi tiết từ API
      const errorMessage = error.response?.data?.error || error.message || 'Có lỗi xảy ra khi xóa giao dịch';
      message.error(errorMessage);
    }
  };

  const handleShowDeleteModal = (transactionId: string, type: 'income' | 'expense') => {
    setTransactionToDelete({ id: transactionId, type });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (transactionToDelete) {
      await handleDeleteTransaction(transactionToDelete.id, transactionToDelete.type);
      setShowDeleteModal(false);
      setTransactionToDelete(null);
    }
  };

  const handleIncomeModalSuccess = () => {
    loadTransactions(true);
    // Reload wallets for income transactions
    try {
      incomeService.getWallets().then(walletsData => {
        setWallets(walletsData);
      }).catch(error => {
        console.error('Error reloading wallets:', error);
      });
    } catch (error) {
      console.error('Error reloading wallets:', error);
    }
  };

  const handleExpenseModalSuccess = () => {
    loadTransactions(true);
  };

  const getCategoryById = (id: number) => {
    return categories.find(c => c.id === id);
  };

  const getSourceById = (id: number) => {
    return sources.find(s => s.id === id);
  };

  const getWalletById = (id: number) => {
    return wallets.find(w => w.id === id);
  };

  const getIconContainerClass = (transaction: CombinedTransaction) => {
    if (transaction.type === 'income') {
      return styles.iconContainerIncome;
    } else {
      const category = transaction.category;
      if (category?.name?.toLowerCase().includes('ăn') || category?.name?.toLowerCase().includes('food')) {
        return styles.iconContainerFood;
      } else if (category?.name?.toLowerCase().includes('di chuyển') || category?.name?.toLowerCase().includes('transport')) {
        return styles.iconContainerTransport;
      }
      return styles.iconContainerOther;
    }
  };

  const getTagClass = (transaction: CombinedTransaction) => {
    if (transaction.type === 'income') {
      return styles.incomeTag;
    } else {
      const category = transaction.category;
      if (category?.name?.toLowerCase().includes('ăn') || category?.name?.toLowerCase().includes('food')) {
        return styles.foodTag;
      } else if (category?.name?.toLowerCase().includes('di chuyển') || category?.name?.toLowerCase().includes('transport')) {
        return styles.transportTag;
      }
      return styles.otherTag;
    }
  };

  const getAmountClass = (transaction: CombinedTransaction) => {
    if (transaction.type === 'income') {
      return styles.amountIncome;
    } else {
      return styles.amountExpense;
    }
  };

  const getTransactionIcon = (transaction: CombinedTransaction) => {
    if (transaction.type === 'income') {
      return <ArrowUpOutlined />;
    } else {
      const category = transaction.category;
      return category?.icon || '💰';
    }
  };

  const getTransactionTypeText = (transaction: CombinedTransaction) => {
    if (transaction.type === 'income') {
      return 'Thu nhập';
    } else {
      return transaction.category?.name || 'Chi tiêu';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Nhóm giao dịch theo ngày
  const groupTransactionsByDate = () => {
    const groups: { [key: string]: CombinedTransaction[] } = {};
    
    combinedTransactions.forEach(transaction => {
      const date = dayjs(transaction.date).format('DD/MM/YYYY');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(transaction);
    });

    return Object.entries(groups).sort(([a], [b]) => 
      dayjs(b, 'DD/MM/YYYY').diff(dayjs(a, 'DD/MM/YYYY'))
    );
  };

  const handleTransactionClick = (transaction: CombinedTransaction) => {
    setSelectedTransaction(transaction);
    setIsDetailModalOpen(true);
  };

  // Throttle scroll để tránh gọi quá nhiều
  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    if (scrollPercentage > 0.8 && hasMore && !loadingMore && !loading) {
      loadMore();
    }
  }, [hasMore, loadingMore, loading]);

  const renderWalletLinks = (transactionId: number) => {
    const links = walletLinks[transactionId];
    if (!links || links.length === 0) return null;

    return (
      <div style={{ marginTop: 8 }}>
        {links.map((link) => (
          <Tag
            key={link.id}
            color="blue"
            icon={<WalletOutlined />}
            style={{ marginBottom: 4 }}
          >
            {link.wallet_name} ({link.wallet_source_name}): {formatCurrency(link.amount)}
          </Tag>
        ))}
      </div>
    );
  };

  const renderTransactionCard = (transaction: CombinedTransaction) => {
    return (
      <div 
        key={transaction.id} 
        className={styles.transactionCard}
        onClick={() => handleTransactionClick(transaction)}
        style={{ cursor: 'pointer' }}
      >
        <div className={`${styles.iconContainer} ${getIconContainerClass(transaction)}`}>
          {getTransactionIcon(transaction)}
        </div>
        
        <div className={styles.transactionDetails}>
          <p className={styles.transactionTitle}>
            {transaction.notes || `${getTransactionTypeText(transaction)}`}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span className={`${styles.category} ${getTagClass(transaction)}`}>
              {getTransactionIcon(transaction)} {getTransactionTypeText(transaction)}
            </span>
            {transaction.type === 'income' && transaction.wallet && (
              <span className={`${styles.source} ${getTagClass(transaction)}`}>
                <WalletOutlined /> {transaction.wallet.name}
              </span>
            )}
            
          </div>
        </div>
        
        <p className={`${styles.amount} ${getAmountClass(transaction)}`}>
          {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
        </p>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <DollarOutlined />
      </div>
      <div className={styles.emptyText}>
        Chưa có giao dịch nào
      </div>
      <div className={styles.emptySubtext}>
        Bắt đầu thêm giao dịch đầu tiên của bạn
      </div>
    </div>
  );

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => prev.subtract(1, 'month'));
    setCurrentPage(1);
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => prev.add(1, 'month'));
    setCurrentPage(1);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ 
      ...prev, 
      [key]: value 
    }));
    setCurrentPage(1);
  };

  const handleListHeaderClick = () => {
    setShowTypeFilter(!showTypeFilter);
  };

  const handleTypeSelect = (type: 'income' | 'expense' | undefined) => {
    handleFilterChange('type', type);
    setShowTypeFilter(false);
  };

  const getSelectedTypeName = () => {
    if (!filters.type) return 'Tất cả giao dịch';
    return filters.type === 'income' ? 'Thu nhập' : 'Chi tiêu';
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleFilterApply = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const getFilterCount = () => {
    let count = 0;
    if (filters.type) count++;
    if (filters.category_id) count++;
    if (filters.source_id) count++;
    if (filters.wallet_id) count++;
    if (filters.start_date) count++;
    if (filters.end_date) count++;
    if (filters.search) count++;
    return count;
  };

  const handleDetailModalEdit = () => {
    if (selectedTransaction) {
      if (selectedTransaction.type === 'income') {
        handleEditIncomeTransaction(selectedTransaction.transaction as IncomeTransaction);
      } else {
        handleEditExpenseTransaction(selectedTransaction.transaction as ExpenseTransaction);
      }
    }
  };

  const handleDetailModalDelete = () => {
    if (selectedTransaction) {
      handleShowDeleteModal(selectedTransaction.id, selectedTransaction.type);
      setIsDetailModalOpen(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.mobileShell}>
        {/* App Header */}
        <header className={styles.appHeader}>
          <div className={styles.dateSelector}>
            <LeftOutlined onClick={handlePreviousMonth} />
            <span>{currentMonth.format('MMMM YYYY')}</span>
            <RightOutlined onClick={handleNextMonth} />
          </div>
        </header>

        {/* Transaction List */}
        <main className={styles.transactionList}>
          <div className={styles.listHeader}>
            <div className={styles.headerLeft} onClick={handleListHeaderClick}>
              <h3>{getSelectedTypeName()}</h3>
              <DownOutlined style={{ 
                transform: showTypeFilter ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            </div>
            <div className={styles.headerRight}>
              <div className={styles.addButton} onClick={handleAddIncomeTransaction}>
                <PlusOutlined />
              </div>
              <div className={styles.addExpenseButton} onClick={handleAddExpenseTransaction}>
                <ArrowDownOutlined />
              </div>
              <div className={styles.filterButton} onClick={handleFilterModalOpen}>
                <FilterOutlined />
                {getFilterCount() > 0 && (
                  <span className={styles.filterBadge}>{getFilterCount()}</span>
                )}
              </div>
            </div>
          </div>

          {/* Type Filter Dropdown */}
          {showTypeFilter && (
            <div className={styles.typeFilterDropdown}>
              <div 
                className={styles.typeFilterOption}
                onClick={() => handleTypeSelect(undefined)}
              >
                <span>Tất cả giao dịch</span>
              </div>
              <div 
                className={styles.typeFilterOption}
                onClick={() => handleTypeSelect('income')}
              >
                <span><ArrowUpOutlined /> Thu nhập</span>
              </div>
              <div 
                className={styles.typeFilterOption}
                onClick={() => handleTypeSelect('expense')}
              >
                <span><ArrowDownOutlined /> Chi tiêu</span>
              </div>
            </div>
          )}

          {/* Scrollable Transaction Content */}
          <div className={styles.transactionContent} onScroll={handleScroll}>
            {combinedTransactions.length > 0 ? (
              <>
                {groupTransactionsByDate().map(([date, transactions]) => (
                  <div key={date} className={styles.transactionGroup}>
                    <p className={styles.groupDate}>{date}</p>
                    {transactions.map(renderTransactionCard)}
                  </div>
                ))}
                {loadingMore && (
                  <div className={styles.loadingMore}>
                    <div className={styles.loadingSpinner}></div>
                    <span>Đang tải thêm...</span>
                  </div>
                )}
                {!hasMore && combinedTransactions.length > 0 && (
                  <div className={styles.noMoreData}>
                    <span>Đã tải hết dữ liệu ({combinedTransactions.length}/{total} mục)</span>
                  </div>
                )}
              </>
            ) : (
              renderEmptyState()
            )}
          </div>
        </main>
      </div>

      {/* Modal thêm/sửa giao dịch thu nhập */}
      <IncomeTransactionModalMobile
        isOpen={isIncomeModalOpen}
        onClose={() => setIsIncomeModalOpen(false)}
        transaction={editingIncomeTransaction}
        onSuccess={handleIncomeModalSuccess}
      />

      {/* Modal thêm/sửa giao dịch chi tiêu */}
      <ExpenseTransactionModalMobile
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
        transaction={editingExpenseTransaction}
        onSuccess={handleExpenseModalSuccess}
      />

      {/* Modal chi tiết giao dịch */}
      {selectedTransaction && (
        selectedTransaction.type === 'income' ? (
          <IncomeDetailModal
            isOpen={isDetailModalOpen}
            onClose={() => setIsDetailModalOpen(false)}
            transaction={selectedTransaction.transaction as IncomeTransaction}
            source={selectedTransaction.source}
            wallet={selectedTransaction.wallet}
            onEdit={handleDetailModalEdit}
            onDelete={handleDetailModalDelete}
            onReloadWallets={() => {
              try {
                incomeService.getWallets().then(walletsData => {
                  setWallets(walletsData);
                }).catch(error => {
                  console.error('Error reloading wallets:', error);
                });
              } catch (error) {
                console.error('Error reloading wallets:', error);
              }
            }}
          />
        ) : (
          <ExpenseDetailModal
            isOpen={isDetailModalOpen}
            onClose={() => setIsDetailModalOpen(false)}
            transaction={selectedTransaction.transaction as ExpenseTransaction}
            category={selectedTransaction.category}
            onEdit={handleDetailModalEdit}
            onDelete={handleDetailModalDelete}
          />
        )
      )}

      {/* Modal xác nhận xóa */}
      <NotiModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Xác nhận xóa"
        message="Bạn có chắc chắn muốn xóa giao dịch này?"
        confirmText="Xóa"
        cancelText="Hủy"
      />
    </div>
  );
};
